import refs from '../helpers/refs'

class CSupportMenu {
  constructor (el) {
    this.$root = el
    this.$refs = refs(this.$root)

    this.tablist = document.querySelectorAll('[role="tablist"]')[0]
    this.tabs = null
    this.panels = null
    this.delay = this.determineDelay()

    // For easy reference
    this.keys = {
      end: 35,
      home: 36,
      left: 37,
      up: 38,
      right: 39,
      down: 40,
      delete: 46
    }

    // Add or substract depending on key pressed
    this.direction = {
      37: -1,
      38: -1,
      39: 1,
      40: 1
    }

    this.init()
  }

  init () {
    this.generateArrays()

    // Bind listeners
    for (let i = 0; i < this.tabs.length; ++i) {
      this.addListeners(i)
    }
  }

  generateArrays () {
    this.tabs = document.querySelectorAll('[role="tab"]')
    this.panels = document.querySelectorAll('[role="tabpanel"]')
  }

  addListeners (index) {
    this.tabs[index].addEventListener('click', this.clickEventListener.bind(this))
    this.tabs[index].addEventListener('keydown', this.keydownEventListener.bind(this))
    this.tabs[index].addEventListener('keyup', this.keyupEventListener.bind(this))

    // Build an array with all tabs (<button>s) in it
    this.tabs[index].index = index
  }

  // When a tab is clicked, activateTab is fired to activate it
  clickEventListener (event) {
    // sometimes event.target is the fa icon instead of the button
    let tab = event.target
    while (tab.nodeName !== 'BUTTON') {
      tab = tab.parentNode
    }
    this.activateTab(tab, false)
  }

  // Handle keydown on tabs
  keydownEventListener (event) {
    const key = event.keyCode

    switch (key) {
      case this.keys.end:
        event.preventDefault()
        // Activate last tab
        this.activateTab(this.tabs[this.tabs.length - 1])
        break
      case this.keys.home:
        event.preventDefault()
        // Activate first tab
        this.activateTab(this.tabs[0])
        break

      // Up and down are in keydown
      // because we need to prevent page scroll >:)
      case this.keys.up:
      case this.keys.down:
        this.determineOrientation(event)
        break
    }
  }

  // Handle keyup on tabs
  keyupEventListener (event) {
    const key = event.keyCode

    switch (key) {
      case this.keys.left:
      case this.keys.right:
        this.determineOrientation(event)
        break
      case this.keys.delete:
        this.determineDeletable(event)
        break
    }
  }

  // When a tablistâ€™s aria-orientation is set to vertical,
  // only up and down arrow should function.
  // In all other cases only left and right arrow function.
  determineOrientation (event) {
    const key = event.keyCode
    const vertical = this.tablist.getAttribute('aria-orientation') === 'vertical'
    let proceed = false

    if (vertical) {
      if (key === this.keys.up || key === this.keys.down) {
        event.preventDefault()
        proceed = true
      }
    } else {
      proceed = false
      if (key === this.keys.left || key === this.keys.right) {
        proceed = true
      }
    }

    if (proceed) {
      this.switchTabOnArrowPress(event)
    }
  }

  // Either focus the next, previous, first, or last tab
  // depening on key pressed
  switchTabOnArrowPress (event) {
    const pressed = event.keyCode

    for (let x = 0; x < this.tabs.length; x++) {
      this.tabs[x].addEventListener('focus', this.focusEventHandler.bind(this))
    }

    if (this.direction[pressed]) {
      const target = event.target
      if (target.index !== undefined) {
        if (this.tabs[target.index + this.direction[pressed]]) {
          this.tabs[target.index + this.direction[pressed]].focus()
        } else if (pressed === this.keys.left || pressed === this.keys.up) {
          this.focusLastTab()
        } else if (pressed === this.keys.right || pressed === this.keys.down) {
          this.focusFirstTab()
        }
      }
    }
  }

  // Activates any given tab panel
  activateTab (tab, setFocus) {
    setFocus = setFocus || true
    // Deactivate all other tabs
    this.deactivateTabs()

    // Remove tabindex attribute
    tab.removeAttribute('tabindex')

    // Set the tab as selected
    tab.setAttribute('aria-selected', 'true')

    // Get the value of aria-controls (which is an ID)
    const controls = tab.getAttribute('aria-controls')

    // Remove hidden attribute from tab panel to make it visible
    document.getElementById(controls).removeAttribute('hidden')

    // Set focus when required
    if (setFocus) {
      tab.focus()
    }
  }

  // Deactivate all tabs and tab panels
  deactivateTabs () {
    for (let t = 0; t < this.tabs.length; t++) {
      this.tabs[t].setAttribute('tabindex', '-1')
      this.tabs[t].setAttribute('aria-selected', 'false')
      this.tabs[t].removeEventListener('focus', this.focusEventHandler)
    }

    for (let p = 0; p < this.panels.length; p++) {
      this.panels[p].setAttribute('hidden', 'hidden')
    }
  }

  // Make a guess
  focusFirstTab () {
    this.tabs[0].focus()
  }

  // Make a guess
  focusLastTab () {
    this.tabs[this.tabs.length - 1].focus()
  }

  // Detect if a tab is deletable
  determineDeletable (event) {
    const target = event.target

    if (target.getAttribute('data-deletable') !== null) {
      // Delete target tab
      this.deleteTab(event, target)

      // Update arrays related to tabs widget
      this.generateArrays()

      // Activate the closest tab to the one that was just deleted
      if (target.index - 1 < 0) {
        this.activateTab(this.tabs[0])
      } else {
        this.activateTab(this.tabs[target.index - 1])
      }
    }
  }

  // Deletes a tab and its panel
  deleteTab (event) {
    const target = event.target
    const panel = document.getElementById(target.getAttribute('aria-controls'))

    target.parentElement.removeChild(target)
    panel.parentElement.removeChild(panel)
  }

  // Determine whether there should be a delay
  // when user navigates with the arrow keys
  determineDelay () {
    const hasDelay = this.tablist.hasAttribute('data-delay')
    let delay = 0

    if (hasDelay) {
      const delayValue = this.tablist.getAttribute('data-delay')
      if (delayValue) {
        delay = delayValue
      } else {
        // If no value is specified, default to 300ms
        delay = 300
      }
    }

    return delay
  }

  //
  focusEventHandler (event) {
    const target = event.target

    setTimeout(this.checkTabFocus, this.delay, target)
  }

  // Only activate tab on focus if it still has focus after the delay
  checkTabFocus (target) {
    const focused = document.activeElement

    if (target === focused) {
      this.activateTab(target, false)
    }
  }
}

// Keep this last
document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('[data-c-support-menu]')
    .forEach(el => new CSupportMenu(el))
}, { once: true })
